// ============================================
// NOTIFICATIONS PAGE
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import {
  Bell,
  Check,
  CheckCheck,
  Loader2,
  Briefcase,
  UserCheck,
  Star,
  Shield,
  Award,
  Trash2,
} from 'lucide-react';
import { PageHeader, ConfirmDialog } from '@/components';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
  useModal,
} from '@/hooks';
import { formatRelativeTime, cn } from '@/utils';
import { useState } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/notifications')({
  component: NotificationsPage,
});

function NotificationsPage() {
  const { t } = useTranslation('panel');
  const { data: notificationsResponse, isLoading } = useNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();
  const deleteModal = useModal();
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);

  const rawNotifications = (notificationsResponse as any)?.data || notificationsResponse;
  const notifications: any[] = Array.isArray(rawNotifications) ? rawNotifications : [];
  const unreadCount = notifications.filter((n: any) => !n.isRead).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'APPLICATION_RECEIVED':
        return Briefcase;
      case 'APPLICATION_STATUS_CHANGED':
        return UserCheck;
      case 'NEW_REVIEW':
        return Star;
      case 'VERIFICATION_APPROVED':
        return Shield;
      case 'BADGE_EARNED':
        return Award;
      default:
        return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'APPLICATION_RECEIVED':
        return 'bg-blue-100 text-blue-600';
      case 'APPLICATION_STATUS_CHANGED':
        return 'bg-green-100 text-green-600';
      case 'NEW_REVIEW':
        return 'bg-yellow-100 text-yellow-600';
      case 'VERIFICATION_APPROVED':
        return 'bg-purple-100 text-purple-600';
      case 'BADGE_EARNED':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const handleDelete = (id: string) => {
    setSelectedNotificationId(id);
    deleteModal.open();
  };

  const confirmDelete = () => {
    if (selectedNotificationId) {
      deleteNotification.mutate(selectedNotificationId);
      deleteModal.close();
      setSelectedNotificationId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('notifications.title')}
        description={unreadCount > 0 ? t('notifications.unreadCount', { count: unreadCount }) : t('notifications.allRead')}
        actions={
          unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              className="btn-secondary"
            >
              {markAllAsRead.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCheck className="w-4 h-4" />
              )}
              {t('notifications.markAllRead')}
            </button>
          )
        }
      />

      {notifications.length === 0 ? (
        <div className="bg-white rounded-maritime border border-gray-200 p-12 text-center">
          <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('notifications.empty.title')}</h3>
          <p className="text-gray-500">
            {t('notifications.empty.description')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-maritime border border-gray-200 divide-y divide-gray-100">
          {notifications.map((notification: any) => {
            const Icon = getNotificationIcon(notification.type);
            const colorClass = getNotificationColor(notification.type);

            return (
              <div
                key={notification.id}
                className={cn(
                  'p-4 flex items-start gap-4 transition-colors',
                  !notification.isRead && 'bg-maritime-50/50'
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                    !notification.isRead ? colorClass : 'bg-gray-100 text-gray-500'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p
                        className={cn(
                          'text-sm',
                          !notification.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'
                        )}
                      >
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {formatRelativeTime(notification.createdAt)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!notification.isRead && (
                        <button
                          onClick={() => markAsRead.mutate(notification.id)}
                          disabled={markAsRead.isPending}
                          className="p-2 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-full transition-colors"
                          title={t('notifications.markRead')}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title={t('notifications.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={deleteModal.isOpen}
        onOpenChange={(open) => !open && deleteModal.close()}
        onConfirm={confirmDelete}
        title={t('notifications.deleteDialog.title')}
        description={t('notifications.deleteDialog.description')}
        confirmText={t('notifications.deleteDialog.confirm')}
        variant="danger"
      />
    </div>
  );
}
